import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, View } from 'react-native';

import { Text, useTheme } from '@/components/ui';
import type { ApplicationChecklistItem } from '@/services/data-service';

/**
 * The cheat sheet (#369).
 *
 * Every request type carries its own submission requirements — Cameras needs the completed
 * form, a plat map with the location marked, photos of the site as it stands, photos of the
 * cameras themselves, and a note on how wiring will be concealed. Those rules live in the
 * ruleset (`required_attachments_json`, migration 206); this is where a reviewer sees them
 * without going to look them up, which is the difference between the rule being followed
 * and the rule existing.
 *
 * An item satisfied by an ASSIGNED FILE is shown differently from one someone ticked. Both
 * count, but only one can be checked against what was actually submitted — a hand-tick is a
 * promise, a file is evidence.
 */
export interface ApplicationChecklistProps {
	items: ApplicationChecklistItem[];
	complete: boolean;
	canUpdate: boolean;
	busy?: boolean;
	onToggle: (key: string, checked: boolean) => void;
}

export const ApplicationChecklist: React.FC<ApplicationChecklistProps> = ({
	items, complete, canUpdate, busy = false, onToggle,
}) => {
	const theme = useTheme();
	if (items.length === 0) return null;

	const outstanding = items.filter((i) => i.required && !i.checked).length;

	return (
		<View testID="application-checklist">
			<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', flex: 1 }}>Submission requirements</Text>
				<View
					style={{
						paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12,
						backgroundColor: complete ? '#e8f5e9' : '#fff3e0',
					}}
				>
					<Text style={{ fontSize: 12, fontWeight: '700', color: complete ? '#2e7d32' : '#e65100' }}>
						{complete ? 'Complete' : `${outstanding} outstanding`}
					</Text>
				</View>
			</View>

			{items.map((item) => (
				<Pressable
					key={item.key}
					// A file-satisfied item is not hand-untickable: the way to undo it is to
					// unassign the file, which is where the truth lives.
					onPress={() => { if (canUpdate && !item.satisfiedByFile && !busy) onToggle(item.key, !item.checked); }}
					disabled={!canUpdate || item.satisfiedByFile || busy}
					testID={`checklist-item-${item.key}`}
					style={{
						flexDirection: 'row', alignItems: 'flex-start',
						// Real padding — RN-Web drops hitSlop, so this box IS the tap target.
						paddingVertical: 8, paddingHorizontal: 6, borderRadius: 6,
					}}
				>
					<Ionicons
						name={item.checked ? 'checkbox' : 'square-outline'}
						size={19}
						color={item.checked ? theme.colors.primary : theme.colors.onSurfaceVariant}
						style={{ marginRight: 10, marginTop: 1 }}
					/>
					<View style={{ flex: 1, minWidth: 0 }}>
						<Text style={{ color: theme.colors.onSurface }}>
							{item.label}
							{item.required ? '' : ' (optional)'}
						</Text>
						{item.satisfiedByFile ? (
							<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginTop: 1 }}>
								Answered by an attached file
							</Text>
						) : null}
					</View>
				</Pressable>
			))}
		</View>
	);
};

export default ApplicationChecklist;
